import axios from 'axios';
import TenancyPortalAPI from '../api/TenancyPortal';
import TwoFactorRequiredError from '../api/TwoFactorRequiredError';
import {
  isArray,
  deleteCookie,
  setCookie,
  clearTrustedDeviceToken,
  getLoginRateLimitMessage,
  getTrustedDeviceToken,
  storeTrustedDeviceToken,
} from '../utils/helpers';
import {KEY_AUTHENTICATED, KEY_AUTHENTICATION_TOKEN} from '../utils/constants';

const setTwoFactorEnrolmentRequired = channels => dispatch =>
  dispatch({type: 'TWO_FACTOR_ENROLMENT_REQUIRED', channels});

export const clearTwoFactorEnrolment = () => dispatch =>
  dispatch({type: 'TWO_FACTOR_ENROLMENT_CLEARED'});

const setLoggingIn = () => dispatch => dispatch({type: 'LOGGING_IN'});
const setRegistering = () => dispatch => dispatch({type: 'REGISTERING'});
const setLoggedIn = authenticated => dispatch =>
  dispatch({type: 'LOGIN_SUCCESS', authenticated});

const setLoggedOut = () => dispatch => dispatch({type: 'LOGOUT'});
const setRegistered = authenticated => dispatch =>
  dispatch({type: 'REGISTRATION_SUCCESS', authenticated});
const setLoginError = error => dispatch =>
  dispatch({type: 'LOGIN_ERROR', error});

const setRegistrationError = error => dispatch =>
  dispatch({type: 'REGISTRATION_ERROR', error});
const setTwoFactorRequired = twoFactor => dispatch =>
  dispatch({type: 'TWO_FACTOR_REQUIRED', twoFactor});
const setTwoFactorError = error => dispatch =>
  dispatch({type: 'TWO_FACTOR_ERROR', error});

export const clearTwoFactor = () => dispatch =>
  dispatch({type: 'TWO_FACTOR_CLEARED'});

export const abandonTwoFactor = message => dispatch =>
  dispatch(setLoginError({message}));

export const setSessionTime = time => dispatch =>
  dispatch({type: 'SET_SESSION_TIME', time});

export const setProfileImageUrl = url => dispatch =>
  dispatch({
    type: 'SET_PROFILE_PHOTO_URL',
    profileImageUrl: url,
  });

const setProfile = data => dispatch => {
  // for some reason profile_photo_id comes as a URL,
  // while every other image is a file ID.
  // The POST endpoint expects an ID, so I have to remove this
  // from here and store the URL somewhere else
  // or it will mess everything up...
  const profile = {...data};
  // eslint-disable-next-line camelcase
  dispatch(setProfileImageUrl(data.profile.profile_photo_id));
  delete profile.profile.profile_photo_id;
  dispatch({type: 'SET_PROFILE_DATA', data: profile});
};

const setProfileError = error => dispatch =>
  dispatch({type: 'SET_PROFILE_ERROR', error});

export const startApplication = started => async dispatch =>
  dispatch({type: 'USER_HAS_STARTED_APPLICATION', started});

export const skippedRegistration = skipped => async dispatch =>
  dispatch({type: 'USER_HAS_SKIPPED_REGISTRATION', skipped});

export const fetchProfileData = () => async dispatch => {
  TenancyPortalAPI.get(
    'tenant?include=profile,identity,addresses,residents,references,pets,pet_references,documents,guarantor'
  )
    .then(({data}) => {
      dispatch(setLoggedIn(true));
      dispatch(setSessionTime(new Date()));
      dispatch(setProfile(data));
      dispatch(setProfileError(null));
    })
    .catch(error => {
      deleteCookie(KEY_AUTHENTICATION_TOKEN);
      deleteCookie(KEY_AUTHENTICATED);
      return dispatch(setProfileError(error));
    });
};

// login and two-factor verify both return the same token payload
const establishSession = (response, callback) => async dispatch => {
  setCookie(KEY_AUTHENTICATION_TOKEN, response.access_token);
  setCookie(KEY_AUTHENTICATED, 'true');
  dispatch(setLoggedIn(true));
  dispatch(setSessionTime(new Date()));
  if (typeof callback === 'function') {
    callback.call(null);
  }
};

const establishPendingEnrolmentSession = response => async dispatch => {
  setCookie(KEY_AUTHENTICATION_TOKEN, response.access_token);
  setCookie(KEY_AUTHENTICATED, 'true');
  dispatch(setTwoFactorEnrolmentRequired(response.two_factor_channels || []));
};

export const doLogin =
  (credentials, callback = undefined) =>
  async dispatch => {
    dispatch(setLoggingIn(true));
    const deviceToken = getTrustedDeviceToken();
    return TenancyPortalAPI.post('auth/login', {
      ...credentials,
      ...(deviceToken ? {device_token: deviceToken} : {}),
    })
      .then(response => {
        if (response.two_factor_enrolment_required) {
          return dispatch(establishPendingEnrolmentSession(response));
        }
        return dispatch(establishSession(response, callback));
      })
      .catch(error => {
        deleteCookie(KEY_AUTHENTICATION_TOKEN);
        deleteCookie(KEY_AUTHENTICATED);
        if (error instanceof TwoFactorRequiredError) {
          // a challenge despite the token means the API has stopped honouring it
          if (deviceToken) {
            clearTrustedDeviceToken();
          }
          dispatch(setTwoFactorRequired(error.getChallenge()));
          return null;
        }
        const {errors} = error;
        if (error.code === 429) {
          dispatch(
            setLoginError({
              name: error.name,
              code: error.code,
              message: getLoginRateLimitMessage(error),
            })
          );
          return errors || null;
        }
        dispatch(setLoginError(error));
        return errors || null;
      });
  };

// resolves to {verified, code, errors} so the form can tell a rejected code
// apart from a failed request
export const doVerifyTwoFactor =
  (
    {challenge, code, trustDevice = false, useRecoveryCode = false},
    callback = undefined
  ) =>
  async dispatch => {
    dispatch(setLoggingIn(true));
    return TenancyPortalAPI.post('auth/two-factor/verify', {
      challenge,
      code,
      // the API rejects anything that is not a real boolean
      trust_device: Boolean(trustDevice),
      use_recovery_code: Boolean(useRecoveryCode),
    })
      .then(async response => {
        // only ever returned here, and only once
        storeTrustedDeviceToken(response.device_token);
        await dispatch(establishSession(response, callback));
        return {verified: true};
      })
      .catch(error => {
        dispatch(setTwoFactorError(error));
        const {errors} = error;
        return {
          verified: false,
          code: error.code,
          errors:
            errors && Object.keys(errors).length
              ? errors
              : {code: error.message},
        };
      });
  };

export const doResendTwoFactor =
  ({challenge, channel = undefined}) =>
  async dispatch =>
    TenancyPortalAPI.post('auth/two-factor/resend', {
      challenge,
      ...(channel ? {channel} : {}),
    })
      .then(response => {
        const sent = {
          challenge: response.challenge || challenge,
          channel: response.channel,
          destination: response.destination || null,
          expires_at: response.expires_at || null,
          can_use_email: response.can_use_email || false,
        };
        dispatch({type: 'TWO_FACTOR_UPDATED', twoFactor: sent});
        return {sent: true, ...sent};
      })
      .catch(error => ({
        sent: false,
        code: error.code,
        message: error.message,
        errors: error.errors || {},
      }));

export const doStartTwoFactorEnrolment =
  ({channel}) =>
  async () =>
    TenancyPortalAPI.post('auth/two-factor', {channel})
      .then(response => ({started: true, ...response}))
      .catch(error => ({
        started: false,
        code: error.code,
        message: error.message,
        errors: error.errors || {},
      }));

export const doConfirmTwoFactorEnrolment =
  ({challenge, code}) =>
  async () =>
    TenancyPortalAPI.post('auth/two-factor/confirm', {
      challenge,
      code: String(code).trim(),
    })
      .then(response => ({
        confirmed: true,
        recovery_codes: response.recovery_codes || [],
        ...response,
      }))
      .catch(error => ({
        confirmed: false,
        code: error.code,
        message: error.message,
        errors:
          error.errors && Object.keys(error.errors).length
            ? error.errors
            : {code: error.message},
      }));

export const completeTwoFactorEnrolment =
  (callback = undefined) =>
  async dispatch => {
    dispatch(clearTwoFactorEnrolment());
    dispatch(setLoggedIn(true));
    dispatch(setSessionTime(new Date()));
    if (typeof callback === 'function') {
      callback.call(null);
    }
  };

export const doLogout = () => async dispatch => {
  dispatch(setLoggingIn(true));
  return (
    TenancyPortalAPI.get('auth/logout')
      .then(() => {
        deleteCookie(KEY_AUTHENTICATION_TOKEN);
        deleteCookie(KEY_AUTHENTICATED);
        dispatch(setSessionTime(null));
        dispatch(setLoggedOut());
      })
      // eslint-disable-next-line no-console
      .catch(error => console.error(error))
  );
};

export const doRegistration =
  (values, callback = undefined) =>
  async dispatch => {
    dispatch(setRegistering(true));
    return TenancyPortalAPI.post('tenant/register', values)
      .then(() => {
        dispatch(setRegistered(true));
        dispatch(setLoggingIn(true));
        return TenancyPortalAPI.post('auth/login', {
          username: values.email,
          password: values.password,
        })
          .then(async response => {
            if (response.two_factor_enrolment_required) {
              await dispatch(establishPendingEnrolmentSession(response));
              return 'success';
            }
            await dispatch(establishSession(response, callback));
            return 'success';
          })
          .catch(error => {
            if (error instanceof TwoFactorRequiredError) {
              dispatch(setTwoFactorRequired(error.getChallenge()));
              return null;
            }
            dispatch(setLoginError(error));
            const {errors} = error;
            return errors || null;
          });
      })
      .catch(error => {
        dispatch(setRegistrationError(error));
        const {errors} = error;
        return errors || null;
      });
  };

export const doPetDelete = async id =>
  TenancyPortalAPI.delete(`tenant/pets/${id}`).then(response => response);

export const doPetDeleteBulk = async ids =>
  new Promise((resolve, reject) => {
    if (isArray(ids) && ids.length >= 0) {
      axios
        .all(ids.map(id => doPetDelete(id)))
        .then(response => {
          resolve(response);
        })
        .catch(e => {
          reject(e);
        });
    } else {
      reject();
    }
  });

export const doResidentDelete = async id =>
  TenancyPortalAPI.delete(`tenant/residents/${id}`).then(response => response);

export const doResidentDeleteBulk = async ids =>
  new Promise((resolve, reject) => {
    if (isArray(ids) && ids.length >= 0) {
      axios
        .all(ids.map(id => doResidentDelete(id)))
        .then(response => {
          resolve(response);
        })
        .catch(e => {
          reject(e);
        });
    } else {
      reject();
    }
  });

export const doVehicleDelete = async id =>
  TenancyPortalAPI.delete(`tenant/vehicles/${id}`).then(response => response);

export const doDocumentDelete = async id =>
  TenancyPortalAPI.delete(`tenant/documents/${id}`).then(response => response);

export const doReferenceDelete = async id =>
  TenancyPortalAPI.delete(`tenant/references/${id}`).then(response => response);

export const doPetReferenceDelete = async id =>
  TenancyPortalAPI.delete(`tenant/pet_references/${id}`).then(
    response => response
  );

export const doPetReferenceBulkDelete = async ids =>
  new Promise((resolve, reject) => {
    if (isArray(ids) && ids.length >= 0) {
      axios
        .all(ids.map(id => doPetReferenceDelete(id)))
        .then(response => {
          resolve(response);
        })
        .catch(e => {
          reject(e);
        });
    } else {
      reject();
    }
  });

export const doAddressDelete = async id =>
  TenancyPortalAPI.delete(`tenant/addresses/${id}`).then(response => response);

export const addFile = (id, url) => dispatch =>
  dispatch({type: 'ADD_FILE', file: {id, url}});

export const getFileURL = async fileID =>
  TenancyPortalAPI.get(`tenant/file/link/${fileID}`)
    .then(response => response.url)
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error('Failed to open attachment', error);
      return null;
    });

export const setFirstName = firstName => dispatch =>
  dispatch({type: 'SET_FIRST_NAME', firstName});
export const setLastName = lastName => dispatch =>
  dispatch({type: 'SET_LAST_NAME', lastName});
export const setEmail = email => dispatch =>
  dispatch({type: 'SET_EMAIL', email});
export const setMobile = mobile => dispatch =>
  dispatch({type: 'SET_MOBILE', mobile});
